import TooltipWrapper from '@/components/ui/tooltip';
import { getMaxLeverage } from '@/features/trade/order/utils/math';
import useFundedAccount, {
  getCapitalMaxLeverage,
} from '@/hooks/use-funded-account';
import { useMarketStore } from '@/store/use-markets-store';
import { useWebSocketStore } from '@/store/use-websocket-store';
import { abbrFromSym, imgFromSym } from '@/utils/token-symbol';
import { formatNumber } from '@/utils/value-format';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useStore } from 'zustand';
import { MarketsTableVariant } from '../../types/markets';
import FavoriteButton from '../favorite-button';

type Props = {
  symbol: string;
  variant: MarketsTableVariant;
};

export default function Symbol({ symbol, variant }: Props) {
  const { t } = useTranslation();
  const { isFundedOrEval } = useFundedAccount();

  const isolated = useStore(
    useMarketStore(symbol),
    (state) => state.marketSpec.isolated,
  );
  const maxLeverage = useStore(useMarketStore(symbol), (state) => {
    const leverage = getMaxLeverage(state.marketSpec.initMarginRatio);
    if (isFundedOrEval && leverage?.bigint !== undefined) {
      const capitalMaxLeverage = getCapitalMaxLeverage(symbol);
      if (leverage.bigint > BigInt(capitalMaxLeverage) * 10n ** 2n) {
        return capitalMaxLeverage.toString();
      }
    }
    return leverage?.decimal;
  });

  useEffect(() => {
    const { increaseDependenciesForSymbol, decreaseDependenciesForSymbol } =
      useWebSocketStore.getState();
    increaseDependenciesForSymbol(symbol);
    return () => decreaseDependenciesForSymbol(symbol);
  }, [symbol]);

  return (
    <div className="flex h-full items-center gap-3">
      {variant !== 'watchList' && (
        <div className="md:mr-1.5">
          <FavoriteButton symbol={symbol} size={14} />
        </div>
      )}
      <img
        src={imgFromSym(symbol)}
        alt="symbol"
        className="h-5 w-5 rounded-full object-contain"
      />
      <span className="font-sans">{abbrFromSym(symbol)}</span>
      <div className="rounded bg-primary px-2">
        <span className="font-mono text-sm text-primary-foreground">
          {formatNumber(maxLeverage, {
            style: 'leverage',
            digits: 0,
          })}
        </span>
      </div>
      {isolated && (
        <TooltipWrapper
          content={
            <span>
              {t('marketsPage.isolatedTooltip')}{' '}
              <Link
                className="underline"
                target="_blank"
                rel="noreferrer"
                to="https://docs.vest.exchange/overview/traders/perpetual-contract-specifications"
                onClick={(e) => e.stopPropagation()}
              >
                {t('marketsPage.docs')}
              </Link>{' '}
              {t('marketsPage.forDetails')}
            </span>
          }
        >
          <div className="hidden rounded bg-primary px-2 sm:block">
            <span className="font-mono text-xs uppercase leading-none text-primary-foreground sm:text-sm">
              {t('marketsPage.isolated')}
            </span>
          </div>
        </TooltipWrapper>
      )}
    </div>
  );
}
